import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Trash2, Save, Loader2, Eye, Code, Copy, Image as ImageIcon, Upload, X, Check, ExternalLink, PauseCircle, PlayCircle, FileText, Star, Send } from 'lucide-react';
import { toast } from 'sonner';
import PublicFormRenderer from '@/components/forms/PublicFormRenderer';
import { publicUrl as buildPublicUrl } from '@/lib/publicUrl';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('masterToken') || localStorage.getItem('master_token') || localStorage.getItem('auth_token') || localStorage.getItem('token')}` });

const FIELD_GROUPS = [
  { group: 'Basic inputs', items: [
    { v: 'text', label: 'Short Text' },
    { v: 'email', label: 'Email' },
    { v: 'textarea', label: 'Long Text' },
    { v: 'number', label: 'Number' },
    { v: 'phone', label: 'Phone' },
    { v: 'date', label: 'Date' },
  ]},
  { group: 'Choice', items: [
    { v: 'select', label: 'Dropdown' },
    { v: 'radio', label: 'Multiple Choice' },
    { v: 'checkbox', label: 'Checkboxes' },
    { v: 'rating', label: 'Star Rating' },
  ]},
  { group: 'Files & Media', items: [
    { v: 'file', label: 'File Upload' },
    { v: 'image', label: 'Image (display)' },
  ]},
  { group: 'Layout', items: [
    { v: 'heading', label: 'Heading' },
    { v: 'paragraph', label: 'Paragraph' },
  ]},
];

const STATUS_OPTIONS = [
  { v: 'draft', label: 'Draft', icon: FileText, color: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/20', desc: 'Not visible publicly' },
  { v: 'published', label: 'Published', icon: PlayCircle, color: 'bg-green-500/15 text-green-400 border-green-500/20', desc: 'Open for submissions' },
  { v: 'paused', label: 'Paused', icon: PauseCircle, color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20', desc: 'Visitors see "we\'re not collecting right now"' },
  { v: 'closed', label: 'Closed', icon: X, color: 'bg-red-500/15 text-red-400 border-red-500/20', desc: 'Visitors see "this form is closed"' },
];

const newFieldId = () => `f_${Math.random().toString(36).slice(2, 8)}`;

export default function MasterFormBuilder() {
  const navigate = useNavigate();
  const { formId } = useParams();
  const isEdit = !!formId;

  const [form, setForm] = useState({
    title: 'Untitled Form',
    description: '',
    slug: '',
    status: 'draft',
    submit_button_label: 'Submit',
    success_message: 'Thanks! Your response has been recorded.',
    cover_image_url: '',
    accent_color: '#FACC15',
    send_email_to: '',
    autoresponder_enabled: false,
    autoresponder_email_field_id: '',
    autoresponder_subject: 'Thanks for reaching out!',
    autoresponder_body: '<p>Hi {{name}},</p>\n<p>Thanks for reaching out — we received your message and will be in touch within 1 business day.</p>\n<p>— The EntrepreNatives Team</p>',
    autoresponder_from_name: 'EntrepreNatives',
    fields: [],
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [savedSlug, setSavedSlug] = useState(null);
  const [showPublishedModal, setShowPublishedModal] = useState(false);
  const [showAddFieldMenu, setShowAddFieldMenu] = useState(false);
  const coverInputRef = useRef(null);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await fetch(`${API}/master/forms/${formId}`, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          setForm({
            title: data.title || '',
            description: data.description || '',
            slug: data.slug || '',
            status: data.status || (data.is_published ? 'published' : 'draft'),
            submit_button_label: data.submit_button_label || 'Submit',
            success_message: data.success_message || 'Thanks!',
            cover_image_url: data.cover_image_url || '',
            accent_color: data.accent_color || '#FACC15',
            send_email_to: data.send_email_to || '',
            autoresponder_enabled: !!data.autoresponder_enabled,
            autoresponder_email_field_id: data.autoresponder_email_field_id || '',
            autoresponder_subject: data.autoresponder_subject || 'Thanks for reaching out!',
            autoresponder_body: data.autoresponder_body || '<p>Hi {{name}},</p>\n<p>Thanks for reaching out — we received your message and will be in touch within 1 business day.</p>\n<p>— The EntrepreNatives Team</p>',
            autoresponder_from_name: data.autoresponder_from_name || 'EntrepreNatives',
            fields: data.fields || [],
          });
          setSavedSlug(data.slug);
        }
      } finally { setLoading(false); }
    })();
  }, [formId, isEdit]);

  const addField = (type) => {
    setShowAddFieldMenu(false);
    const f = { id: newFieldId(), type, label: '', placeholder: '', required: false };
    if (type === 'text') f.label = 'Short answer';
    else if (type === 'email') f.label = 'Email';
    else if (type === 'textarea') f.label = 'Long answer';
    else if (type === 'number') f.label = 'Number';
    else if (type === 'phone') f.label = 'Phone';
    else if (type === 'date') f.label = 'Date';
    else if (type === 'select') { f.label = 'Pick one'; f.options = ['Option 1', 'Option 2']; }
    else if (type === 'radio') { f.label = 'Pick one'; f.options = ['Option 1', 'Option 2']; }
    else if (type === 'checkbox') { f.label = 'Pick any'; f.options = ['Option 1', 'Option 2']; }
    else if (type === 'rating') { f.label = 'Rating'; f.max_rating = 5; }
    else if (type === 'file') { f.label = 'Upload files'; f.max_files = 3; f.help = 'Up to 25MB per file.'; }
    else if (type === 'image') f.label = '';
    else if (type === 'heading') f.label = 'Section title';
    else if (type === 'paragraph') f.label = 'Add some descriptive text here.';
    setForm((p) => ({ ...p, fields: [...p.fields, f] }));
  };

  const updateField = (id, patch) => setForm((p) => ({ ...p, fields: p.fields.map(f => f.id === id ? { ...f, ...patch } : f) }));
  const removeField = (id) => setForm((p) => ({ ...p, fields: p.fields.filter(f => f.id !== id) }));
  const moveField = (id, dir) => setForm((p) => {
    const i = p.fields.findIndex(f => f.id === id);
    if (i < 0) return p;
    const j = i + dir;
    if (j < 0 || j >= p.fields.length) return p;
    const next = [...p.fields];
    [next[i], next[j]] = [next[j], next[i]];
    return { ...p, fields: next };
  });

  const uploadImage = async (file, onDone) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API}/master/forms/upload-image`, { method: 'POST', headers: authHeaders(), body: fd });
    const data = await res.json().catch(() => ({}));
    if (res.ok) onDone(data.url);
    else toast.error(data.detail || 'Image upload failed');
  };

  const save = async ({ overrideStatus } = {}) => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const body = JSON.stringify({ ...form, status: overrideStatus || form.status });
      const url = isEdit ? `${API}/master/forms/${formId}` : `${API}/master/forms`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', ...authHeaders() }, body });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Save failed'); return; }
      toast.success(isEdit ? 'Form saved' : 'Form created');
      setSavedSlug(data.slug);
      if (overrideStatus) setForm((p) => ({ ...p, status: overrideStatus }));
      if ((overrideStatus || form.status) === 'published') setShowPublishedModal(true);
      if (!isEdit) navigate(`/master/forms/builder/${data.form_id}`);
    } finally { setSaving(false); }
  };

  const publicUrl = savedSlug ? buildPublicUrl(`/forms/${savedSlug}`) : '';
  const embedCode = savedSlug ? `<iframe src="${publicUrl}?embed=1" style="width:100%;min-height:600px;border:0;border-radius:16px;" loading="lazy" title="${form.title}"></iframe>` : '';

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#A78BFA]" /></div>;

  const currentStatus = STATUS_OPTIONS.find((s) => s.v === form.status) || STATUS_OPTIONS[0];

  return (
    <div className="space-y-5" data-testid="master-form-builder">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <Link to="/master/forms" className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-white"><ArrowLeft size={14} /> All forms</Link>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-[11px] font-bold border ${currentStatus.color} flex items-center gap-1.5`} data-testid="status-badge">
            <currentStatus.icon size={12} /> {currentStatus.label}
          </span>
          {savedSlug && (
            <a href={`/forms/${savedSlug}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white text-xs font-semibold flex items-center gap-1.5"><ExternalLink size={11} /> Open</a>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* ─── Left: Editor ─── */}
        <div className="space-y-5">
          {/* Form settings */}
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
            <h2 className="font-syne text-lg font-bold text-white mb-3">Form Settings</h2>
            <div className="space-y-3">
              <Input label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="builder-title" />
              <Input label="Slug (URL)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} placeholder="auto-generated from title" help={savedSlug ? `/forms/${savedSlug}` : ''} />
              <Textarea label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />

              <div>
                <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider block mb-1">Cover image (optional, shown at top of form)</label>
                {form.cover_image_url ? (
                  <div className="relative rounded-xl overflow-hidden border border-white/10">
                    <img src={form.cover_image_url.startsWith('http') ? form.cover_image_url : `${process.env.REACT_APP_BACKEND_URL}${form.cover_image_url}`} alt="cover" className="w-full max-h-48 object-cover block" />
                    <button onClick={() => setForm({ ...form, cover_image_url: '' })} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-500" data-testid="remove-cover"><X size={14} /></button>
                  </div>
                ) : (
                  <button type="button" onClick={() => coverInputRef.current?.click()} className="w-full px-4 py-3 rounded-xl bg-white/5 border-2 border-dashed border-white/15 hover:border-[#A78BFA]/60 hover:bg-white/10 text-zinc-200 text-sm flex items-center justify-center gap-2 transition" data-testid="upload-cover-btn">
                    <Upload size={14} /> Add cover image
                  </button>
                )}
                <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f, (url) => setForm((p) => ({ ...p, cover_image_url: url }))); e.target.value = ''; }} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input label="Submit button label" value={form.submit_button_label} onChange={(v) => setForm({ ...form, submit_button_label: v })} />
                <div>
                  <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider block mb-1">Accent color</label>
                  <div className="flex gap-2">
                    <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="w-10 h-10 rounded-lg cursor-pointer bg-transparent border border-white/15" />
                    <input value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white text-sm font-mono" />
                  </div>
                </div>
              </div>

              <Textarea label="Success message" value={form.success_message} onChange={(v) => setForm({ ...form, success_message: v })} rows={2} />
              <Input label="Email submissions to (optional)" value={form.send_email_to} onChange={(v) => setForm({ ...form, send_email_to: v })} placeholder="you@entreprenatives.com" type="email" />

              <div>
                <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider block mb-1">Status</label>
                <div className="grid grid-cols-2 gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <button key={s.v} type="button" onClick={() => setForm({ ...form, status: s.v })}
                      className={`px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 border transition ${form.status === s.v ? `${s.color} border-current` : 'bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10'}`}
                      data-testid={`status-${s.v}`}>
                      <s.icon size={13} />
                      <div className="text-left flex-1 min-w-0">
                        <div>{s.label}</div>
                        <div className="text-[9px] opacity-70 truncate font-normal">{s.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Autoresponder */}
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="autoresponder-card">
            <div className="flex items-start justify-between mb-3 gap-3">
              <div>
                <h2 className="font-syne text-lg font-bold text-white flex items-center gap-2"><Send size={16} className="text-[#A78BFA]" /> Autoresponder</h2>
                <p className="text-[#9CA3AF] text-xs mt-0.5">Send an instant "thank you" email to whoever submits the form.</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.autoresponder_enabled}
                  onChange={(e) => setForm({ ...form, autoresponder_enabled: e.target.checked })}
                  className="w-4 h-4 rounded"
                  data-testid="autoresponder-toggle"
                />
                <span className="text-xs text-zinc-200">{form.autoresponder_enabled ? 'On' : 'Off'}</span>
              </label>
            </div>

            {form.autoresponder_enabled && (
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider block mb-1">Visitor's email is in this field</label>
                  <select
                    value={form.autoresponder_email_field_id}
                    onChange={(e) => setForm({ ...form, autoresponder_email_field_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white text-sm"
                    data-testid="autoresponder-email-field"
                  >
                    <option value="" className="bg-[#13153A]">Auto-detect first email field</option>
                    {form.fields.filter(f => !['image', 'heading', 'paragraph', 'file', 'rating'].includes(f.type)).map(f => (
                      <option key={f.id} value={f.id} className="bg-[#13153A]">
                        {f.label || f.id} ({f.type}{f.type === 'email' ? ' · recommended' : ''})
                      </option>
                    ))}
                  </select>
                  <p className="text-[10px] text-[#9CA3AF] mt-1">If left blank, we'll use the first email-type field with a value.</p>
                </div>

                <Input label="From name (sender display)" value={form.autoresponder_from_name} onChange={(v) => setForm({ ...form, autoresponder_from_name: v })} placeholder="EntrepreNatives" />
                <Input label="Subject line" value={form.autoresponder_subject} onChange={(v) => setForm({ ...form, autoresponder_subject: v })} placeholder="Thanks for reaching out!" testid="autoresponder-subject" />

                <div>
                  <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider block mb-1">Email body (HTML supported)</label>
                  <textarea
                    rows={8}
                    value={form.autoresponder_body}
                    onChange={(e) => setForm({ ...form, autoresponder_body: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white text-xs font-mono leading-relaxed"
                    data-testid="autoresponder-body"
                  />
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span className="text-[10px] text-[#9CA3AF]">Tokens:</span>
                    {form.fields.filter(f => !['image', 'heading', 'paragraph'].includes(f.type)).map(f => {
                      const tok = `{{${(f.label || f.id).toLowerCase()}}}`;
                      return (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => setForm((p) => ({ ...p, autoresponder_body: (p.autoresponder_body || '') + ' ' + tok }))}
                          className="px-2 py-0.5 rounded text-[10px] bg-purple-500/15 hover:bg-purple-500/25 text-purple-300 font-mono"
                        >{tok}</button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-[#9CA3AF] mt-1.5">Tokens are auto-replaced with the visitor's input. Click to insert.</p>
                </div>
              </div>
            )}
          </div>
          {/* Field manager */}
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-syne text-lg font-bold text-white">Fields ({form.fields.length})</h2>
              <div className="relative">
                <button onClick={() => setShowAddFieldMenu(!showAddFieldMenu)} className="px-3 py-1.5 rounded-lg bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-bold" data-testid="add-field-btn">+ Add field</button>
                {showAddFieldMenu && (
                  <div className="absolute right-0 top-full mt-1 z-30 w-64 bg-[#1A1A3E] border border-white/15 rounded-xl shadow-2xl p-2 max-h-[320px] overflow-y-auto" data-testid="add-field-menu">
                    {FIELD_GROUPS.map((g) => (
                      <div key={g.group} className="mb-2 last:mb-0">
                        <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider px-2 py-1">{g.group}</p>
                        {g.items.map((t) => (
                          <button key={t.v} onClick={() => addField(t.v)} className="w-full text-left px-3 py-1.5 rounded-lg text-xs text-white hover:bg-purple-500/15" data-testid={`add-${t.v}`}>{t.label}</button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {form.fields.length === 0 && <p className="text-sm text-[#9CA3AF] text-center py-6">Add a field to start.</p>}

            <div className="space-y-3">
              {form.fields.map((f, i) => (
                <FieldEditor
                  key={f.id}
                  field={f}
                  index={i}
                  total={form.fields.length}
                  onUpdate={(patch) => updateField(f.id, patch)}
                  onRemove={() => removeField(f.id)}
                  onMove={(dir) => moveField(f.id, dir)}
                  uploadImage={uploadImage}
                />
              ))}
            </div>
          </div>

          {/* Save bar */}
          <div className="flex items-center gap-2 sticky bottom-4 bg-[#0B0D2B]/90 backdrop-blur-xl py-3 px-4 rounded-2xl border border-white/10 z-20">
            <button onClick={() => save()} disabled={saving} className="flex-1 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="builder-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => save({ overrideStatus: 'published' })} disabled={saving} className="flex-1 px-4 py-2.5 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="builder-publish">
              <PlayCircle size={14} /> {form.status === 'published' ? 'Save & Republish' : 'Publish'}
            </button>
          </div>
        </div>

        {/* ─── Right: Live Preview ─── */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-[#9CA3AF] uppercase tracking-wider flex items-center gap-1.5"><Eye size={12} /> Live Preview</span>
            </div>
            <div className="bg-[#13153A]/60 rounded-xl border border-purple-500/20 overflow-hidden" data-testid="builder-preview">
              {form.cover_image_url && (
                <img src={form.cover_image_url.startsWith('http') ? form.cover_image_url : `${process.env.REACT_APP_BACKEND_URL}${form.cover_image_url}`} alt="cover" className="w-full max-h-40 object-cover" />
              )}
              <div className="p-5">
                <h2 className="font-syne text-2xl font-black text-white mb-2 break-words">{form.title || 'Untitled'}</h2>
                {form.description && <p className="text-zinc-300 text-sm mb-4 leading-relaxed">{form.description}</p>}
                {form.fields.length > 0 ? (
                  <PublicFormRenderer
                    form={{ ...form, slug: savedSlug || 'preview' }}
                    embedded
                  />
                ) : (
                  <p className="text-sm text-[#9CA3AF] py-6 text-center">Add fields to see the preview.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Published modal ─── */}
      {showPublishedModal && savedSlug && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setShowPublishedModal(false)}>
          <div onClick={(e) => e.stopPropagation()} className="max-w-lg w-full rounded-2xl bg-[#13153A] border border-green-500/40 shadow-[0_30px_80px_-15px_rgba(34,197,94,0.5)] p-6 sm:p-8" data-testid="publish-success-modal">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-500/15 text-green-400 mb-4">
              <Check size={26} />
            </div>
            <h2 className="font-syne text-2xl font-black text-white mb-2">Your form is live!</h2>
            <p className="text-zinc-300 text-sm mb-6">Share this link anywhere — social media, email, your website, even on a printed flyer with a QR code.</p>

            <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider block mb-1">Public link</label>
            <div className="flex gap-2 mb-4">
              <input readOnly value={publicUrl} className="flex-1 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs font-mono" data-testid="publish-modal-url" />
              <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Link copied'); }} className="px-4 py-2.5 rounded-lg bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-bold flex items-center gap-1.5" data-testid="publish-modal-copy"><Copy size={12} /> Copy</button>
            </div>

            <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider block mb-1">Embed on any external website</label>
            <textarea readOnly rows={3} value={embedCode} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs font-mono mb-2" />
            <button onClick={() => { navigator.clipboard.writeText(embedCode); toast.success('Embed code copied'); }} className="w-full px-3 py-2 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 text-xs font-bold flex items-center justify-center gap-1.5"><Code size={11} /> Copy embed code</button>

            <div className="grid grid-cols-2 gap-2 mt-5">
              <a href={publicUrl} target="_blank" rel="noreferrer" className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white text-sm font-semibold flex items-center justify-center gap-1.5"><ExternalLink size={13} /> Open form</a>
              <button onClick={() => setShowPublishedModal(false)} className="px-4 py-2.5 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-black text-sm font-bold">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Per-field editor card (separate component so display-only fields stay clean)
// ──────────────────────────────────────────────────────────────────────────
function FieldEditor({ field, index, total, onUpdate, onRemove, onMove, uploadImage }) {
  const f = field;
  const isDisplayOnly = ['image', 'heading', 'paragraph'].includes(f.type);
  const typeLabel = (FIELD_GROUPS.flatMap(g => g.items).find(t => t.v === f.type) || {}).label || f.type;
  const imgRef = useRef(null);

  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-3" data-testid={`builder-field-${f.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-purple-300 font-bold">{typeLabel}</span>
        <div className="flex items-center gap-1">
          <button onClick={() => onMove(-1)} disabled={index === 0} className="text-[#9CA3AF] hover:text-white disabled:opacity-30 px-1">↑</button>
          <button onClick={() => onMove(1)} disabled={index === total - 1} className="text-[#9CA3AF] hover:text-white disabled:opacity-30 px-1">↓</button>
          <button onClick={onRemove} className="text-red-400 hover:text-red-300 ml-1" data-testid={`remove-field-${f.id}`}><Trash2 size={13} /></button>
        </div>
      </div>

      {f.type === 'image' ? (
        <div>
          {f.image_url ? (
            <div className="relative rounded-lg overflow-hidden border border-white/10 mb-2">
              <img src={f.image_url.startsWith('http') ? f.image_url : `${process.env.REACT_APP_BACKEND_URL}${f.image_url}`} alt={f.label || ''} className="w-full max-h-40 object-cover block" />
              <button onClick={() => onUpdate({ image_url: '' })} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-500"><X size={13} /></button>
            </div>
          ) : (
            <button type="button" onClick={() => imgRef.current?.click()} className="w-full px-4 py-3 rounded-xl bg-white/5 border-2 border-dashed border-white/15 hover:border-[#A78BFA]/60 text-zinc-200 text-xs flex items-center justify-center gap-2 mb-2" data-testid={`upload-image-${f.id}`}>
              <ImageIcon size={14} /> Upload image
            </button>
          )}
          <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) uploadImage(file, (url) => onUpdate({ image_url: url })); e.target.value = ''; }} />
          <Input label="Caption (optional)" value={f.label || ''} onChange={(v) => onUpdate({ label: v })} compact />
        </div>
      ) : f.type === 'heading' || f.type === 'paragraph' ? (
        f.type === 'heading' ? (
          <Input label="Heading text" value={f.label} onChange={(v) => onUpdate({ label: v })} compact />
        ) : (
          <Textarea label="Paragraph text" value={f.label} onChange={(v) => onUpdate({ label: v })} rows={3} />
        )
      ) : (
        <>
          <Input label="Label" value={f.label} onChange={(v) => onUpdate({ label: v })} compact />
          {!['rating'].includes(f.type) && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Input label="Placeholder" value={f.placeholder || ''} onChange={(v) => onUpdate({ placeholder: v })} compact />
              <label className="flex items-center gap-2 text-xs text-zinc-200 cursor-pointer mt-5">
                <input type="checkbox" checked={!!f.required} onChange={(e) => onUpdate({ required: e.target.checked })} />
                Required
              </label>
            </div>
          )}
          {f.type === 'rating' && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Input label="Max stars" type="number" value={f.max_rating || 5} onChange={(v) => onUpdate({ max_rating: parseInt(v) || 5 })} compact />
              <label className="flex items-center gap-2 text-xs text-zinc-200 cursor-pointer mt-5">
                <input type="checkbox" checked={!!f.required} onChange={(e) => onUpdate({ required: e.target.checked })} />
                Required
              </label>
            </div>
          )}
          {f.type === 'file' && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Input label="Max files" type="number" value={f.max_files || 3} onChange={(v) => onUpdate({ max_files: Math.max(1, parseInt(v) || 1) })} compact />
              <Input label="Help text" value={f.help || ''} onChange={(v) => onUpdate({ help: v })} compact />
            </div>
          )}
          {['select', 'radio', 'checkbox'].includes(f.type) && (
            <div className="mt-2">
              <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider block mb-1.5">Options</label>
              <OptionList
                options={f.options || []}
                onChange={(opts) => onUpdate({ options: opts })}
                fieldId={f.id}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Iter 175f — Form Builder option list.
// Each option is its own row with its own input + remove button. Pressing
// Enter on the last row's input creates a new option below it and focuses
// it, matching how Google Forms / Typeform handle option entry. Solves
// the previous "single-textarea, one-per-line" UX which forced users to
// edit a freeform string and never let them add more than 2 default options
// without retyping.
function OptionList({ options, onChange, fieldId }) {
  const update = (i, v) => {
    const next = options.slice();
    next[i] = v;
    onChange(next);
  };
  const remove = (i) => {
    if (options.length === 1) return; // keep at least 1 row
    const next = options.slice();
    next.splice(i, 1);
    onChange(next);
  };
  const add = () => {
    onChange([...options, `Option ${options.length + 1}`]);
    // Focus the new row on the next tick.
    setTimeout(() => {
      const inputs = document.querySelectorAll(`[data-opt-field="${fieldId}"]`);
      const last = inputs[inputs.length - 1];
      if (last) last.focus();
    }, 0);
  };
  const onKey = (e, i) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      add();
    } else if (e.key === 'Backspace' && !options[i] && options.length > 1) {
      e.preventDefault();
      remove(i);
    }
  };
  return (
    <div className="space-y-1.5">
      {(options.length ? options : ['']).map((opt, i) => (
        <div key={i} className="flex gap-1.5 items-center" data-testid={`option-row-${fieldId}-${i}`}>
          <span className="text-[10px] text-[#6B7280] w-4 text-right tabular-nums">{i + 1}.</span>
          <input
            data-opt-field={fieldId}
            data-testid={`option-input-${fieldId}-${i}`}
            value={opt}
            onChange={(e) => update(i, e.target.value)}
            onKeyDown={(e) => onKey(e, i)}
            placeholder={`Option ${i + 1}`}
            className="flex-1 px-2 py-1.5 rounded-lg bg-white/5 border border-white/15 text-white text-xs focus:outline-none focus:border-[#A78BFA]/60"
          />
          <button
            type="button"
            onClick={() => remove(i)}
            disabled={options.length <= 1}
            className="w-6 h-6 rounded-md text-[#9CA3AF] hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40 disabled:hover:bg-transparent text-sm leading-none"
            data-testid={`option-remove-${fieldId}-${i}`}
            title="Remove option"
          >×</button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="text-[11px] font-semibold text-[#A78BFA] hover:text-[#C4B5FD] mt-1 flex items-center gap-1"
        data-testid={`option-add-${fieldId}`}
      >+ Add option <span className="text-[9px] opacity-60">(or press Enter)</span></button>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, type = 'text', help, compact, testid }) {
  return (
    <div>
      <label className={`block mb-1 ${compact ? 'text-[10px] text-[#9CA3AF]' : 'text-[10px] text-[#9CA3AF] uppercase tracking-wider'}`}>{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || ''} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white text-sm focus:outline-none focus:border-[#A78BFA]/60" data-testid={testid} />
      {help && <p className="text-[10px] text-[#A78BFA] mt-1">{help}</p>}
    </div>
  );
}

function Textarea({ label, value, onChange, rows = 3 }) {
  return (
    <div>
      <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider block mb-1">{label}</label>
      <textarea rows={rows} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white text-sm focus:outline-none focus:border-[#A78BFA]/60" />
    </div>
  );
}
